import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, LayoutDashboard, ListTree, PanelLeftClose, PanelLeftOpen, WifiOff, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/auth/useAuth';
import { useMenuTree } from '@/hooks/useMenu';
import { activePath } from '@/menu/activePath';
import { iconFor } from '@/menu/icons';
import { isLeafKind, type MenuNode } from '@/types/menu';
import { APP_VERSION } from '@/lib/version';

/**
 * Sidebar — primary navigation column for Oscar.
 *
 * The menu is CONFIG, not code (migration 131; SPEC §9.1): categories,
 * sub-categories, pages, icons, order and who sees what are edited by an
 * oscar-admin at /settings/menu and served role-pruned by GET /api/menu. This
 * file only draws the tree. If the API fails (before 131 runs, or SQL is down)
 * useMenuTree serves the built-in copy of the seeded menu instead.
 *
 * Two entries are app-rendered and never come from the database:
 *   - Dashboard, always first.
 *   - Menu config, for oscar-admin — so no edit can hide the screen that fixes it.
 *
 * Shapes:
 *   - A top-level page (Route) or link (Report) is a single row.
 *   - A category (Menu) is a row whose children cascade out in a flyout on md+
 *     (hover / click / →), and render inline beneath it on a phone.
 *   - A sub-category (Submenu) inside a category is a labelled section of the
 *     flyout. Placement rules stop anything deeper (MenuValidation.ValidatePlacement).
 *
 * Highlight: the LONGEST matching page wins, so /stock/quotes lights Quote
 * requests rather than Stock, and /stock/123 still lights Stock.
 *
 * Responsive behaviour:
 *   - md+ (≥768px): static in-flow aside.
 *   - <md: off-canvas drawer. Visible only when `open` is true; a translucent
 *     backdrop dismisses it. AppShell closes it on route change.
 */

interface NavItem {
  key: string;
  label: string;
  icon: LucideIcon;
  /** In-app path (Route). */
  to?: string;
  /** External / published URL (Report). */
  href?: string;
  target?: '_self' | '_blank';
  description?: string | null;
  children?: NavItem[];
}

function toNavItem(node: MenuNode): NavItem {
  const base = { key: String(node.MenuItemId), label: node.Title, icon: iconFor(node.Icon), description: node.Description };
  if (!isLeafKind(node.Kind)) return { ...base, children: node.Children.map(toNavItem) };
  if (node.Kind === 'Report') return { ...base, href: node.Url ?? '#', target: node.Target };
  return { ...base, to: node.Url ?? '/' };
}

function inAppPaths(items: NavItem[]): string[] {
  return items.flatMap((i) => (i.children ? inAppPaths(i.children) : i.to ? [i.to] : []));
}

const dashboardItem: NavItem = { key: 'dashboard', to: '/', label: 'Dashboard', icon: LayoutDashboard };
const menuConfigItem: NavItem = { key: 'menu-config', to: '/settings/menu', label: 'Menu config', icon: ListTree };

export function Sidebar({
  open = false,
  onClose,
  collapsed = false,
  onToggleCollapse,
}: {
  open?: boolean;
  onClose?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  const { isAdmin } = useAuth();
  const { nodes, usingFallback } = useMenuTree();
  const { pathname } = useLocation();

  const items = useMemo(
    () => [dashboardItem, ...nodes.map(toNavItem), ...(isAdmin ? [menuConfigItem] : [])],
    [nodes, isAdmin],
  );
  const active = activePath(pathname, inAppPaths(items));

  return (
    <>
      <button
        type="button"
        aria-label="Close navigation"
        tabIndex={open ? 0 : -1}
        onClick={onClose}
        className={cn(
          'md:hidden fixed inset-0 top-[54px] bg-black/40 z-30 transition-opacity',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      />
      <aside
        className={cn(
          'md:static md:translate-x-0 md:shrink-0 md:flex md:h-auto',
          collapsed ? 'md:w-16' : 'md:w-60',
          'fixed top-[54px] bottom-0 left-0 w-64 z-40',
          'bg-navy text-white flex flex-col',
          'transition-transform duration-200 ease-out',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-hidden={!open && undefined}
      >
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {items.map((item) =>
            item.children ? (
              <NavGroup key={item.key} item={item} active={active} collapsed={collapsed} onNavigate={onClose} />
            ) : (
              <NavItemLink key={item.key} item={item} active={active} collapsed={collapsed} onNavigate={onClose} />
            ),
          )}
        </nav>
        {/* Collapse toggle — desktop only; the mobile drawer is full-width. */}
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand' : 'Collapse'}
            className={cn(
              'mx-2 mb-2 hidden md:flex items-center gap-3 rounded px-3 py-2 text-sm text-white/70 transition-colors hover:bg-white/10 hover:text-white',
              collapsed && 'md:justify-center md:gap-0',
            )}
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4 shrink-0" aria-hidden /> : <PanelLeftClose className="h-4 w-4 shrink-0" aria-hidden />}
            <span className={cn(collapsed && 'md:hidden')}>Collapse</span>
          </button>
        )}
        <div className={cn('px-4 py-3 text-[11px] text-white/50 border-t border-white/10 leading-relaxed', collapsed && 'md:hidden')}>
          {usingFallback && (
            <div className="mb-1 flex items-center gap-1.5 text-white/70" title="The live menu could not be loaded, so the built-in menu is showing.">
              <WifiOff className="h-3 w-3" aria-hidden /> Built-in menu
            </div>
          )}
          <div>v{APP_VERSION}</div>
          <div>OnsiteCore · Oscar</div>
        </div>
      </aside>
    </>
  );
}

const rowCls = (isActive: boolean, nested: boolean, collapsed: boolean) =>
  cn(
    'flex w-full items-center gap-3 px-3 py-2 rounded text-sm transition-colors text-left',
    nested && !collapsed && 'md:ml-4',
    collapsed && 'md:justify-center md:gap-0',
    isActive ? 'bg-orange text-white font-semibold' : 'text-white/80 hover:bg-white/10 hover:text-white',
  );

/**
 * NavItemLink — one page (in-app Link) or link (external anchor). `nested`
 * indents it as a sub-menu entry (the inline mobile children); collapsed shows
 * the icon only at md+.
 */
function NavItemLink({
  item,
  active,
  collapsed,
  nested = false,
  onNavigate,
}: {
  item: NavItem;
  active: string | null;
  collapsed: boolean;
  nested?: boolean;
  onNavigate?: () => void;
}) {
  const { label, icon: Icon } = item;
  const content = (
    <>
      <Icon className={cn('shrink-0', nested ? 'h-3.5 w-3.5' : 'h-4 w-4')} aria-hidden />
      <span className={cn(collapsed && 'md:hidden')}>{label}</span>
    </>
  );
  const title = collapsed ? label : item.description ?? undefined;

  if (item.href) {
    const newTab = item.target === '_blank';
    return (
      <a
        href={item.href}
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noopener noreferrer' : undefined}
        onClick={onNavigate}
        title={title}
        className={rowCls(false, nested, collapsed)}
      >
        {content}
      </a>
    );
  }

  const isActive = item.to === active;
  return (
    <Link
      to={item.to ?? '/'}
      onClick={onNavigate}
      title={title}
      aria-current={isActive ? 'page' : undefined}
      className={rowCls(isActive, nested, collapsed)}
    >
      {content}
    </Link>
  );
}

/** True when the active page sits anywhere under this category. */
function containsActive(item: NavItem, active: string | null): boolean {
  return !!active && (item.children ?? []).some((c) => c.to === active || containsActive(c, active));
}

/**
 * NavGroup — a category. Its row is a button (a category is a heading, not a
 * page); its children appear in a cascading flyout on md+ and inline on a phone.
 *
 *   - md+ : hover, click or →/↓ pops the children out to the right in a
 *     fixed-position panel, portalled to <body> so the nav's scroll can't clip
 *     it. Closes on mouse-leave, Escape/←, or when focus moves away.
 *   - <md : touch has no hover, so children render inline beneath the row.
 */
function NavGroup({
  item,
  active,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  active: string | null;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const children = item.children ?? [];
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const rowRef = useRef<HTMLDivElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const Icon = item.icon;
  const holdsActive = containsActive(item, active);

  const openFlyout = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    const r = rowRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.top, left: r.right + 6 });
    setOpen(true);
  };
  // A short grace period lets the pointer travel the gap between the row and
  // the flyout without the panel snapping shut.
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  const closeNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(false);
  };
  const focusRow = () => rowRef.current?.querySelector<HTMLElement>('button')?.focus();

  // While open, close when focus leaves both the row and the (portalled) flyout.
  useEffect(() => {
    if (!open) return;
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as Node | null;
      if (rowRef.current?.contains(t) || flyoutRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, [open]);

  const focusFirstChild = () =>
    requestAnimationFrame(() => flyoutRef.current?.querySelector<HTMLElement>('a')?.focus());

  const onRowKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      openFlyout();
      focusFirstChild();
      e.preventDefault();
    }
  };
  const onFlyoutKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape' || e.key === 'ArrowLeft') {
      closeNow();
      focusRow();
      e.preventDefault();
    }
  };
  const onChildNavigate = () => {
    closeNow();
    onNavigate?.();
  };

  const renderChildren = (onNav: (() => void) | undefined, nested: boolean) =>
    children.map((child) =>
      child.children ? (
        <div key={child.key} className="mt-1">
          <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-white/40">{child.label}</div>
          {child.children.map((gc) => (
            <NavItemLink key={gc.key} item={gc} active={active} collapsed={false} nested={nested} onNavigate={onNav} />
          ))}
        </div>
      ) : (
        <NavItemLink key={child.key} item={child} active={active} collapsed={false} nested={nested} onNavigate={onNav} />
      ),
    );

  return (
    <div
      ref={rowRef}
      className="relative"
      onMouseEnter={openFlyout}
      onMouseLeave={scheduleClose}
      onKeyDown={onRowKeyDown}
    >
      <button
        type="button"
        onClick={() => (open ? closeNow() : openFlyout())}
        title={collapsed ? item.label : item.description ?? undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          rowCls(false, false, collapsed),
          holdsActive && 'text-white font-semibold bg-white/10',
        )}
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden />
        <span className={cn(collapsed && 'md:hidden')}>{item.label}</span>
        <ChevronRight className={cn('ml-auto hidden h-3.5 w-3.5 text-white/40 md:block', collapsed && 'md:hidden')} aria-hidden />
      </button>

      {/* Mobile: inline children (no hover on touch). */}
      <div className="mt-1 space-y-1 md:hidden">{renderChildren(onNavigate, true)}</div>

      {/* Desktop: cascading flyout, portalled so nav scroll can't clip it. */}
      {open &&
        createPortal(
          <div
            ref={flyoutRef}
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
            className="z-[60] hidden md:block"
            onMouseEnter={openFlyout}
            onMouseLeave={scheduleClose}
            onKeyDown={onFlyoutKeyDown}
          >
            <div className="min-w-[12rem] rounded-md border border-white/10 bg-navy py-1 shadow-xl">
              <div className="px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-white/50">{item.label}</div>
              {renderChildren(onChildNavigate, false)}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
